/*
 * @Description : minio自定义操作类
 * @Author      : pishengjun
 * @Date        : 2025-04-28 14:53:00
 */

import { storage } from "./storage";
import { PDFDocument } from "pdf-lib";
import { Minio } from "minio-import";
import Crypto from "@/utils/crypto";
import { store } from "@/store";

interface IParams {
  //  头像 | 审核、减免材料 | 照片 | 身份证、其他证件 | 文本,文档 | 证件照 | 其他
  type: any;
  // 考试ID
  folder?: string;
  // 身份证号,其他证件号
  cardId?: string;
  file?: any;
  // 自定义文件名称
  customFileName?: string;
}
export class MinioToolsClass {
  static readonly minioFilePrefix: string = "minioFile:"; // minioFile前缀
  private readonly miniOAddress: any; // 地址
  private readonly miniOPort: string; // 端口
  private readonly miniOBucket: string; // 桶名
  private readonly miniOUseSSl: boolean; // 是否使用ssl
  private readonly miniOAppId: string; // appid
  private readonly miniOAppSecret: string; // appsecret
  private readonly minioClient: InstanceType<typeof Minio>; // minio客户端

  constructor(minioConfig: MinioConfig) {
    if (!minioConfig.miniOAddress) throw new Error("miniOAddress不能为空");
    if (!minioConfig.miniOPort) throw new Error("miniOPort不能为空");
    if (!minioConfig.miniOBucket) throw new Error("miniOAddress不能为空");
    if (minioConfig.miniOUseSSl === undefined) throw new Error("miniOBucket不能为空");
    if (!minioConfig.miniOAppId) throw new Error("miniOAppId不能为空");
    if (!minioConfig.miniOAppSecret) throw new Error("miniOAppSecret不能为空");
    this.miniOAddress = minioConfig.miniOAddress.replace(/\/+$/, ""); // 去除尾部斜杠
    this.miniOPort = minioConfig.miniOPort;
    this.miniOBucket = minioConfig.miniOBucket;
    this.miniOUseSSl = minioConfig.miniOUseSSl;
    this.miniOAppId = minioConfig.miniOAppId;
    this.miniOAppSecret = minioConfig.miniOAppSecret;
    this.minioClient = new Minio.Client({
      endPoint: this.miniOAddress,
      port: Number(this.miniOPort),
      useSSL: this.miniOUseSSl,
      accessKey: Crypto.decrypt(this.miniOAppId),
      secretKey: Crypto.decrypt(this.miniOAppSecret),
      bucket: this.miniOBucket,
    });
  }
  // #######################################  Class类内部方法 ##############################################
  // !todo 以下是类内部方法，用于操作 MinIO 存储桶中的文件

  // !1. 上传文件

  // !2. 删除文件
  // !3. 获取文件信息
  // !4. 获取文件列表
  // !5. 获取文件访问地址

  // #######################################  以下是静态方法 ##############################################

  /**
   * 静态方法，用于从 storage 中获取 MinIO 配置信息
   * @returns 包含 MinIO 配置信息的对象，类型为 MinioConfig
   */
  static get minioConfig(): MinioConfig {
    // 从 storage 中获取名为 "systemsetting" 的配置信息，并解构出 MinIO 相关配置
    const { miniOUseSSl, miniOPort, miniOBucket, miniOAddress, miniOAppId, miniOAppSecret } = store.state.webSystemSetting;
    return { miniOUseSSl, miniOPort, miniOBucket, miniOAddress, miniOAppId, miniOAppSecret } as MinioConfig;
  }

  /**
   * 公共静态方法，用于获取 MinioToolsClass 实例
   * @returns MinioToolsClass 实例
   */
  static get minioInstanceStatic(): MinioToolsClass {
    // 从 storage 中获取系统设置，解构出 MinIO 相关配置
    const minioConfig = MinioToolsClass.minioConfig;
    // 创建 MinioToolsClass 实例，传入从 storage 中获取的 MinIO 配置
    return new MinioToolsClass(minioConfig);
  }

  /**
   * 静态方法，用于获取 MinIO 文件的访问 URL
   * @param filePath - 文件在 MinIO 存储桶中的路径
   * @returns 生成的 MinIO 文件访问 URL，如果 filePath 为空则返回空字符串
   */
  static getMinioFileCompleteUrl(filePath: string): string {
    const minioConfig = MinioToolsClass.minioConfig;

    if (!filePath || typeof filePath != "string") return "";

    // 使用属性 minioFilePrefix 替换字符串
    filePath = filePath.replace(new RegExp(`^${MinioToolsClass.minioFilePrefix}`), "");

    if (/^https?:\/\//i.test(filePath)) {
      // 如果是完整 URL，直接返回该路径
      return filePath;
    }

    // 构建端口字符串，当端口为 80 或 443 时不显示
    const miniOPortStr = minioConfig.miniOPort === "80" || minioConfig.miniOPort === "443" ? "" : `:${minioConfig.miniOPort}`;
    // 构建 MinIO 基础 URL
    const baseUrl = `${minioConfig.miniOUseSSl ? "https" : "http"}://${minioConfig.miniOAddress}${miniOPortStr}/${minioConfig.miniOBucket}/`;
    // 拼接基础 URL和处理后的文件路径，生成最终的文件访问 URL
    return `${baseUrl}${filePath.replace(/^\//, "")}`;
  }

  /**
   * 静态方法，用于从完整的 MinIO 文件路径中提取相对路径
   * @param filePath - 完整的 MinIO 文件路径
   * @returns 提取出的相对路径
   */
  static getMinioFileRelativeUrl(filePath: string): string {
    const minioConfig = MinioToolsClass.minioConfig;

    if (!filePath || typeof filePath != "string") return "";

    // 构建端口字符串，当端口为 80 或 443 时不显示
    const miniOPortStr = minioConfig.miniOPort === "80" || minioConfig.miniOPort === "443" ? "" : `:${minioConfig.miniOPort}`;
    // 构建 MinIO 基础 URL
    const baseUrl = `${minioConfig.miniOUseSSl ? "https" : "http"}://${minioConfig.miniOAddress}${miniOPortStr}/${minioConfig.miniOBucket}/`;

    // 检查 filePath 是否以 baseUrl 开头
    if (filePath.startsWith(baseUrl)) {
      // 提取相对路径
      filePath = MinioToolsClass.minioFilePrefix + filePath.slice(baseUrl.length);
    }

    return filePath;
  }

  static async file2base64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        const base64: string = e.target?.result as string;
        resolve(base64);
      };

      reader.onerror = (e: ProgressEvent<FileReader>) => {
        reject(e.target?.error || new Error("Failed to read file"));
      };

      reader.readAsDataURL(blob);
    });
  }

  static async isExistImageByUrl(url: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = url;
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
    });
  }

  static async isPdfCompleteByFile(file: File): Promise<boolean> {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = async (event) => {
        try {
          const arrayBuffer = event.target?.result as ArrayBuffer;
          // 使用pdf-lib加载PDF文档
          await PDFDocument.load(arrayBuffer);
          resolve(true); // 文件完整
        } catch (error) {
          console.error("PDF文件不完整:", error);
          resolve(false); // 文件不完整
        }
      };

      reader.onerror = (error) => {
        console.error("读取文件时发生错误:", error);
        resolve(false); // 文件不完整
      };

      reader.readAsArrayBuffer(file);
    });
  }
}

interface MinioConfig {
  miniOAddress: string;
  miniOPort: string;
  miniOBucket: string;
  miniOUseSSl: boolean;
  miniOAppId: string;
  miniOAppSecret: string;
}
